import { jsx } from "slate-hyperscript";
import escapeHtml from "escape-html";
import { Text } from "slate";

// 是否是 XML 1.0 允许的字符。规则与后端 SanitizeXMLText 保持一致：
// 保留 tab/换行/回车和正常字符，剔除 C0 控制字符（例如曾经把 RSS 解析弄坏的
// 垂直制表符 U+000B）、非字符 U+FFFE/U+FFFF 以及落单的代理项。
const isValidXmlChar = (cp) =>
  cp === 0x09 ||
  cp === 0x0a ||
  cp === 0x0d ||
  (cp >= 0x20 && cp <= 0xd7ff) ||
  (cp >= 0xe000 && cp <= 0xfffd) ||
  (cp >= 0x10000 && cp <= 0x10ffff);

// 过滤掉 XML 非法字符，保证提交到后端并生成 RSS 的文案可被正常解析。
// for...of 按码点遍历，合法代理对（emoji 等）会整体保留，落单代理项被丢弃。
export const sanitizeXmlText = (text) => {
  if (typeof text !== "string" || text.length === 0) {
    return text;
  }
  let result = "";
  for (const ch of text) {
    if (isValidXmlChar(ch.codePointAt(0))) {
      result += ch;
    }
  }
  return result;
};

// 把加粗/斜体等 mark 递归应用到 text 节点上（跳过 element 本身）
const applyMark = (child, mark) => {
  if (typeof child === "string") {
    return { text: child, ...mark };
  }
  if (Array.isArray(child.children)) {
    return { ...child, children: child.children.map((c) => applyMark(c, mark)) };
  }
  return { ...child, ...mark };
};

const SKIPPED_TAGS = ["STYLE", "SCRIPT", "META", "TITLE", "HEAD", "COLGROUP"];

export const deserialize = (el) => {
  if (el.nodeType === 3) {
    return el.textContent;
  } else if (el.nodeType !== 1) {
    return null;
  }

  const nodeName = el.nodeName;
  if (SKIPPED_TAGS.includes(nodeName)) {
    return null;
  }

  let children = Array.from(el.childNodes)
    .map(deserialize)
    .flat()
    .filter((child) => child !== null);

  if (children.length === 0) {
    children = [{ text: "" }];
  }

  switch (nodeName) {
    case "BODY":
      return jsx("fragment", {}, children);
    case "BR":
      return "\n";
    case "BLOCKQUOTE":
      return jsx("element", { type: "quote" }, children);
    case "P":
      return jsx("element", { type: "paragraph" }, children);
    // 编辑器不支持标题块，粘贴时降级为加粗段落
    case "H1":
    case "H2":
    case "H3":
    case "H4":
    case "H5":
    case "H6":
      return jsx(
        "element",
        { type: "paragraph" },
        children.map((child) => applyMark(child, { bold: true }))
      );
    case "UL":
    case "OL": {
      // 丢弃 <li> 之间由 HTML 缩进产生的空白文本节点
      const items = children.filter(
        (child) => typeof child !== "string" || child.trim() !== ""
      );
      return jsx(
        "element",
        { type: nodeName === "UL" ? "bulleted-list" : "numbered-list" },
        items.length > 0 ? items : [{ text: "" }]
      );
    }
    case "LI":
      return jsx("element", { type: "list-item" }, children);
    case "A":
      return jsx(
        "element",
        { type: "link", url: el.getAttribute("href") },
        children
      );
    case "IMG":
      return jsx(
        "element",
        {
          type: "image",
          url: el.getAttribute("src"),
          alt: el.getAttribute("alt"),
        },
        [{ text: "" }]
      );
    case "STRONG":
    case "B":
      // Google Docs 用 <b style="font-weight:normal"> 包裹整段剪贴板内容，不代表加粗
      if (/font-weight:\s*normal/i.test(el.getAttribute("style") || "")) {
        return children;
      }
      return children.map((child) => applyMark(child, { bold: true }));
    case "EM":
    case "I":
      return children.map((child) => applyMark(child, { italic: true }));
    case "U":
      return children.map((child) => applyMark(child, { underline: true }));
    case "SPAN": {
      // Google Docs / Word 通过内联样式而非语义标签表达格式
      const style = el.getAttribute("style") || "";
      const mark = {};
      if (/font-weight:\s*(bold|bolder|[6-9]00)/i.test(style)) mark.bold = true;
      if (/font-style:\s*italic/i.test(style)) mark.italic = true;
      if (/text-decoration[^;]*underline/i.test(style)) mark.underline = true;
      if (Object.keys(mark).length === 0) {
        return children;
      }
      return children.map((child) => applyMark(child, mark));
    }
    default:
      return children;
  }
};

export const htmlToSlate = (html) => {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const fragment = deserialize(parsed.body);
  if (!Array.isArray(fragment)) {
    return fragment;
  }
  // 含块级元素时，丢弃块之间由 HTML 源码缩进产生的纯空白文本节点
  if (!fragment.some((node) => node.type)) {
    return fragment;
  }
  const cleaned = fragment.filter(
    (node) => node.type || (node.text && node.text.trim() !== "")
  );
  return cleaned.length > 0 ? cleaned : fragment;
};

export const serialize = (node) => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (node.bold) {
      string = `<strong>${string}</strong>`;
    }
    if (node.italic) {
      string = `<em>${string}</em>`;
    }
    if (node.underline) {
      string = `<u>${string}</u>`;
    }
    return string;
  }

  const children = node.children.map((n) => serialize(n)).join("");

  switch (node.type) {
    case "quote":
      return `<blockquote><p>${children}</p></blockquote>`;
    case "paragraph":
      return `<p>${children}</p>`;
    case "link":
      return `<a href="${escapeHtml(node.url)}">${children}</a>`;
    case "image":
      // 还没换成 CDN 地址的占位图（本地预览或 base64）不能写进内容
      if (node.uploadId) {
        return "";
      }
      return `<img src="${escapeHtml(node.url)}" alt="${escapeHtml(
        node.alt || ""
      )}" />`;
    case "bulleted-list":
      return `<ul>${children}</ul>`;
    case "numbered-list":
      return `<ol>${children}</ol>`;
    case "list-item":
      return `<li>${children}</li>`;
    default:
      return children;
  }
};

export const slateToHtml = (value) => {
  return sanitizeXmlText(value.map((node) => serialize(node)).join(""));
};

// 保存前检查编辑器里是否还有没上传到 CDN 的图片，有则返回提示文案
export const getPendingImageUploadWarning = (nodes) => {
  let uploading = 0;
  let failed = 0;
  const walk = (list) =>
    list.forEach((node) => {
      if (node.type === "image" && node.uploadId) {
        if (node.uploadFailed) {
          failed++;
        } else {
          uploading++;
        }
      }
      if (Array.isArray(node.children)) {
        walk(node.children);
      }
    });
  walk(Array.isArray(nodes) ? nodes : []);

  if (uploading > 0) {
    return `还有 ${uploading} 张图片正在上传，请等上传完成后再保存`;
  }
  if (failed > 0) {
    return `有 ${failed} 张图片上传失败，请删除标红的图片后重新插入`;
  }
  return "";
};

// Normalize Slate content to ensure every element has at least one text child
export const normalizeSlateContent = (content) => {
  if (!Array.isArray(content)) {
    return [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ];
  }

  return content.map((node) => {
    if (!node || typeof node !== "object") {
      return {
        type: "paragraph",
        children: [{ text: "" }],
      };
    }

    if (node.type && (!node.children || node.children.length === 0)) {
      return {
        ...node,
        children: [{ text: "" }],
      };
    }

    if (node.type && Array.isArray(node.children)) {
      return {
        ...node,
        children: node.children.map((child) => {
          if (typeof child === "string") {
            return { text: child };
          }
          if (!child || typeof child !== "object") {
            return { text: "" };
          }
          if (child.type && (!child.children || child.children.length === 0)) {
            return {
              ...child,
              children: [{ text: "" }],
            };
          }
          return child;
        }),
      };
    }

    return node;
  });
};
